import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { trackPixel } from '../utils/metaPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const [status, setStatus] = useState('checking');
  const [orderId, setOrderId] = useState(null);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        // Meta Pixel: Purchase (deduped per Stripe session)
        const dedupeKey = `fbq_purchase_${sessionId}`;
        if (!sessionStorage.getItem(dedupeKey)) {
          sessionStorage.setItem(dedupeKey, '1');
          trackPixel('Purchase', {
            value: data.amount_total || 0,
            currency: (data.currency || 'eur').toUpperCase(),
          });
        }
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderIdParam = searchParams.get('order_id');
    setOrderId(orderIdParam);

    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('success');
    }
  }, [searchParams, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center py-12 px-4">
      <Card className="max-w-md w-full rounded-3xl border-[#004E64]/5">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 animate-spin text-[#FF6B35] mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-[#004E64] mb-2">
                {language === 'pt' ? 'Verificando pagamento...' : 'Checking payment...'}
              </h1>
              <p className="text-[#9FA2B4]">
                {language === 'pt' ? 'Por favor, aguarde' : 'Please wait'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-[#2EC4B6]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-[#2EC4B6]" />
              </div>
              <h1 className="text-2xl font-bold text-[#004E64] mb-2">
                {language === 'pt' ? 'Pedido Confirmado!' : 'Order Confirmed!'}
              </h1>
              <p className="text-[#9FA2B4] mb-6">
                {language === 'pt' 
                  ? 'Seu pedido foi recebido e está sendo preparado.'
                  : 'Your order has been received and is being prepared.'}
              </p>
              {orderId && (
                <Link to={`/order-tracking/${orderId}`}>
                  <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full w-full py-6 font-bold">
                    {language === 'pt' ? 'Acompanhar Pedido' : 'Track Order'}
                  </Button>
                </Link>
              )}
              <Link to="/restaurants">
                <Button variant="outline" className="mt-4 rounded-full w-full">
                  {language === 'pt' ? 'Fazer Novo Pedido' : 'Place New Order'}
                </Button>
              </Link>
            </>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#004E64] mb-2">
                {language === 'pt' ? 'Erro no Pagamento' : 'Payment Error'}
              </h1>
              <p className="text-[#9FA2B4] mb-6">
                {language === 'pt' 
                  ? 'Houve um problema com seu pagamento. Por favor, tente novamente.'
                  : 'There was a problem with your payment. Please try again.'}
              </p>
              <Link to="/checkout">
                <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full w-full py-6 font-bold">
                  {language === 'pt' ? 'Tentar Novamente' : 'Try Again'}
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderSuccessPage;
